/**
 * キュー・ジョブ定義。docs/06-async-jobs.md 第2章。
 * 登録側（API）と処理側（ワーカー）がこの定義を共有する。
 */
export const QUEUE = {
    SEARCH: 'search',
    CRAWL: 'crawl',
    GENERATE: 'generate',
    SEND_FORM: 'send-form',
    SEND_MAIL: 'send-mail',
    INBOX: 'inbox',
    ANALYTICS: 'analytics',
    MAINTENANCE: 'maintenance',
    EXPORT: 'export',
};
export const QUEUE_LABELS = {
    search: '収集',
    crawl: 'クロール',
    generate: '文面生成',
    'send-form': 'フォーム送信',
    'send-mail': 'メール送信',
    inbox: '受信取込',
    analytics: '集計',
    maintenance: '保守',
    export: 'エクスポート',
};
/** 並列度と再試行の既定値（docs/06-async-jobs.md 第2章） */
export const QUEUE_DEFAULTS = {
    search: { concurrency: 2, attempts: 3, backoffMs: 5_000 },
    crawl: { concurrency: 5, attempts: 2, backoffMs: 5_000 },
    generate: { concurrency: 3, attempts: 2, backoffMs: 5_000 },
    // 自動再試行しない。重複送信を避けるため（docs/06-async-jobs.md 第2章）
    'send-form': { concurrency: 2, attempts: 1, backoffMs: 0 },
    'send-mail': { concurrency: 3, attempts: 2, backoffMs: 10_000 },
    inbox: { concurrency: 2, attempts: 3, backoffMs: 5_000 },
    analytics: { concurrency: 1, attempts: 2, backoffMs: 30_000 },
    maintenance: { concurrency: 1, attempts: 1, backoffMs: 0 },
    export: { concurrency: 2, attempts: 1, backoffMs: 0 },
};
export const JOB = {
    SEARCH_COLLECT: 'search.collect',
    CRAWL_COMPANY: 'crawl.company',
    CRAWL_PAGE: 'crawl.page',
    AI_GENERATE_MESSAGE: 'ai.generateMessage',
    AI_GENERATE_FOLLOWUP: 'ai.generateFollowup',
    AI_SUMMARIZE_COMPANY: 'ai.summarizeCompany',
    AI_SECURITY_SCAN: 'ai.securityScan',
    SEND_FORM: 'send.form',
    SEND_MAIL: 'send.mail',
    NEWSLETTER_SEND: 'newsletter.send',
    INBOX_FETCH: 'inbox.fetch',
    INBOX_MATCH: 'inbox.match',
    INBOX_CLASSIFY: 'inbox.classify',
    ANALYTICS_DAILY: 'analytics.daily',
    SUGGESTION_DAILY: 'suggestion.daily',
    CAMPAIGN_TICK: 'campaign.tick',
    ACTIVITY_REMINDER: 'activity.reminder',
    QUOTA_CHECK: 'quota.check',
    MAINTENANCE_RETENTION: 'maintenance.retention',
    MAINTENANCE_REQUEUE: 'maintenance.requeue',
    EXPORT_COMPANIES: 'export.companies',
    EXPORT_ANALYTICS: 'export.analytics',
    IMPORT_COMPANIES: 'import.companies',
};
export const JOB_LABELS = {
    'search.collect': '企業の収集',
    'crawl.company': '企業サイトのクロール',
    'crawl.page': 'ページの取得',
    'ai.generateMessage': '文面の生成',
    'ai.generateFollowup': '追客文の生成',
    'ai.summarizeCompany': '企業情報の要約',
    'ai.securityScan': 'セキュリティチェック',
    'newsletter.send': 'メルマガ配信',
    'send.form': 'フォーム送信',
    'send.mail': 'メール送信',
    'inbox.fetch': '受信の取得',
    'inbox.match': '受信の紐付け',
    'inbox.classify': '受信の分類',
    'analytics.daily': '日次集計',
    'suggestion.daily': 'AI 提案の生成',
    'campaign.tick': '送信対象の抽出',
    'activity.reminder': '次回アクションの確認',
    'quota.check': '利用上限の確認',
    'maintenance.retention': '保持期間の整理',
    'maintenance.requeue': '停滞した処理の検出',
    'export.companies': '企業の CSV 出力',
    'export.analytics': '分析の CSV 出力',
    'import.companies': '企業の CSV 取込',
};
/**
 * ジョブ ID は業務キーから決定的に生成する（docs/06-async-jobs.md 2.1）。
 * 同一 ID のジョブは重複登録されない。
 * BullMQ は job ID に ':' を許さないため、区切りには '--' を使う。
 */
const J = (...parts) => parts.join('--');
export const jobIds = {
    crawlCompany: (companyId, day) => J('crawl', companyId, day),
    generateMessage: (campaignId, companyId, version) => J('gen', campaignId, companyId, version),
    generateBulk: (jobRunId, index) => J('gen', 'bulk', jobRunId, index),
    followup: (campaignTargetId, count) => J('followup', campaignTargetId, count),
    send: (campaignTargetId, attemptNo) => J('send', campaignTargetId, attemptNo),
    singleSend: (generatedMessageId, key) => J('send', 'single', generatedMessageId, key),
    newsletterSend: (newsletterId, subscriberId) => J('nl', newsletterId, subscriberId),
    classify: (inboundMessageId) => J('classify', inboundMessageId),
    match: (inboundMessageId) => J('match', inboundMessageId),
    analyticsDaily: (organizationId, date) => J('metrics', organizationId, date),
    collect: (collectionRunId) => J('collect', collectionRunId),
    exportCompanies: (exportId) => J('export', 'companies', exportId),
    importCompanies: (importId) => J('import', 'companies', importId),
    recrawl: (companyId, stamp) => J('crawl', 'manual', companyId, stamp),
    securityScan: (companyId, stamp) => J('secscan', companyId, stamp),
    securityScanBulk: (jobRunId, index) => J('secscan', 'bulk', jobRunId, index),
    inboxFetch: (jobRunId) => J('inbox', 'fetch', jobRunId),
    suggestion: (jobRunId) => J('suggestion', jobRunId),
    repeatable: (jobName, organizationId) => J('repeat', jobName, organizationId),
};
/** 停止フラグの Redis キー（docs/06-async-jobs.md 2.2） */
export const cancelKey = (jobRunId) => `cancel:${jobRunId}`;
/** 定期ジョブ（docs/06-async-jobs.md 7.4） */
export const REPEATABLE_JOBS = [
    { name: JOB.INBOX_FETCH, queue: QUEUE.INBOX, everyMs: 5 * 60_000, label: '受信取込（5分）' },
    { name: JOB.CAMPAIGN_TICK, queue: QUEUE.SEND_MAIL, everyMs: 60_000, label: '送信対象の抽出（1分）' },
    { name: JOB.ACTIVITY_REMINDER, queue: QUEUE.MAINTENANCE, everyMs: 60 * 60_000, label: '次回アクションの通知（1時間）' },
    { name: JOB.ANALYTICS_DAILY, queue: QUEUE.ANALYTICS, cron: '0 3 * * *', label: '日次集計（03:00）' },
    { name: JOB.SUGGESTION_DAILY, queue: QUEUE.GENERATE, cron: '0 4 * * *', label: 'AI 提案生成（04:00）' },
    { name: JOB.MAINTENANCE_RETENTION, queue: QUEUE.MAINTENANCE, cron: '0 5 * * *', label: '保持期間削除（05:00）' },
    { name: JOB.MAINTENANCE_REQUEUE, queue: QUEUE.MAINTENANCE, everyMs: 10 * 60_000, label: '停滞ジョブ検出（10分）' },
    { name: JOB.QUOTA_CHECK, queue: QUEUE.MAINTENANCE, everyMs: 60 * 60_000, label: '利用上限の確認（1時間）' },
];
/** ワーカー役割ごとの担当キュー（環境変数 APP_ROLE） */
export const ROLE_QUEUES = {
    'worker-crawl': [QUEUE.SEARCH, QUEUE.CRAWL],
    'worker-send': [QUEUE.SEND_FORM, QUEUE.SEND_MAIL],
    'worker-ai': [QUEUE.GENERATE, QUEUE.INBOX, QUEUE.ANALYTICS, QUEUE.MAINTENANCE, QUEUE.EXPORT],
    all: [
        QUEUE.SEARCH,
        QUEUE.CRAWL,
        QUEUE.GENERATE,
        QUEUE.SEND_FORM,
        QUEUE.SEND_MAIL,
        QUEUE.INBOX,
        QUEUE.ANALYTICS,
        QUEUE.MAINTENANCE,
        QUEUE.EXPORT,
    ],
};
//# sourceMappingURL=queues.js.map